import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import NorthIndianChart from "@/components/astrology/NorthIndianChart";
import PlanetaryPositions from "@/components/astrology/PlanetaryPositions";
import DashaPeriods from "@/components/astrology/DashaPeriods";
import { Trash2, ChevronLeft, Eye, Star, Loader2, Play, User, Heart, BookOpen, Sparkles, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SESSION_TYPES = {
  personal: { label: "Personal Kundli", short: "Personal", Icon: User, badge: "bg-amber-500/15 text-amber-300 border-amber-500/30", accent: "hover:border-amber-500/40", bar: "bg-amber-400" },
  compatibility: { label: "Marriage Compatibility", short: "Compatibility", Icon: Heart, badge: "bg-pink-500/15 text-pink-300 border-pink-500/30", accent: "hover:border-pink-500/40", bar: "bg-pink-400" },
  lalkitab: { label: "Lal Kitab Analysis", short: "Lal Kitab", Icon: BookOpen, badge: "bg-orange-500/15 text-orange-300 border-orange-500/30", accent: "hover:border-orange-500/40", bar: "bg-orange-500" },
  tarot: { label: "Tarot Reading", short: "Tarot", Icon: Sparkles, badge: "bg-violet-500/15 text-violet-300 border-violet-500/30", accent: "hover:border-violet-500/40", bar: "bg-violet-400" },
};

const typeOf = (chart) => SESSION_TYPES[chart.mode] || SESSION_TYPES.personal;
export const modeKey = (chart) => (SESSION_TYPES[chart.mode] ? chart.mode : "personal");

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const TypeBadge = ({ chart, testId }) => {
  const t = typeOf(chart);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${t.badge}`} data-testid={testId}>
      <t.Icon className="h-3 w-3" />{t.label}
    </span>
  );
};

const SessionCard = ({ chart, onView, onOpen, onDelete }) => {
  const t = typeOf(chart);
  const k = chart.kundli_data;
  const k2 = chart.kundli_data_2;
  const isCompat = chart.mode === "compatibility";
  return (
    <Card className={`relative overflow-hidden bg-black/30 border-white/10 transition-colors duration-300 ${t.accent}`} data-testid={`saved-chart-${chart.id}`}>
      <div className={`absolute left-0 top-0 h-full w-1 ${t.bar}`} />
      <CardContent className="p-5 pl-6">
        <div className="flex items-start justify-between mb-3 gap-2">
          <TypeBadge chart={chart} testId={`session-type-badge-${chart.id}`} />
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(chart.id); }}
            className="text-slate-600 hover:text-red-400 hover:bg-red-500/10 h-7 w-7 -mt-1" data-testid={`delete-chart-${chart.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <h3 className="font-cinzel text-amber-400 font-bold tracking-wide text-sm" data-testid={`session-name-${chart.id}`}>{chart.name || "Unknown"}</h3>
        <p className="text-xs text-slate-500 font-data mt-1">{k?.date_of_birth} | {k?.place_of_birth}</p>
        {isCompat && k2 && <p className="text-xs text-slate-500 font-data">{k2.date_of_birth} | {k2.place_of_birth}</p>}
        <div className="flex items-center gap-3 mt-3 mb-4 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1" data-testid={`session-date-${chart.id}`}><Calendar className="h-3 w-3" />{formatDate(chart.created_at)}</span>
          {chart.messages?.length > 0 && <span>{chart.messages.length} messages</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 border-white/10 text-slate-300 hover:text-amber-400 hover:border-amber-500/30 gap-1.5 text-xs"
            onClick={() => onView(chart)} data-testid={`view-chart-${chart.id}`}>
            <Eye className="h-3.5 w-3.5" />View
          </Button>
          <Button variant="outline" size="sm" className="flex-1 border-amber-500/20 text-amber-400 hover:bg-amber-500/10 gap-1.5 text-xs"
            onClick={() => onOpen(chart)} data-testid={`open-session-${chart.id}`}>
            <Play className="h-3.5 w-3.5" />Open
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ChartDetail = ({ selected, onBack, onOpen }) => {
  const k = selected.kundli_data;
  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="saved-chart-detail">
      <Button variant="ghost" onClick={onBack} className="text-slate-400 hover:text-amber-400 mb-4 gap-2" data-testid="back-btn">
        <ChevronLeft className="h-4 w-4" />Back to Saved Sessions
      </Button>
      <div className="glass rounded-2xl p-5 glow-gold">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div>
            <TypeBadge chart={selected} testId="detail-type-badge" />
            <h2 className="font-cinzel text-amber-400 text-xl font-bold tracking-wide mt-2">{selected.name}</h2>
            <p className="text-xs text-slate-500 font-data mt-1">{k?.date_of_birth} | {k?.time_of_birth} | {k?.place_of_birth}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => onOpen(selected)}
            className="text-xs gap-1.5 border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="open-session-btn">
            <Play className="h-3.5 w-3.5" />Open Session
          </Button>
        </div>
        <Tabs defaultValue="chart" className="w-full">
          <TabsList className="w-full bg-black/30 border border-white/5 mb-4">
            <TabsTrigger value="chart" className="flex-1 text-xs data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400">Chart</TabsTrigger>
            <TabsTrigger value="navamsha" className="flex-1 text-xs data-[state=active]:bg-violet-500/15 data-[state=active]:text-violet-400">Navamsha</TabsTrigger>
            <TabsTrigger value="planets" className="flex-1 text-xs data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400">Planets</TabsTrigger>
            <TabsTrigger value="dashas" className="flex-1 text-xs data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400">Dashas</TabsTrigger>
          </TabsList>
          <TabsContent value="chart"><NorthIndianChart houses={k?.houses} /></TabsContent>
          <TabsContent value="navamsha">
            <p className="text-xs text-violet-400 font-cinzel tracking-wide mb-2">Navamsha (D-9)</p>
            {k?.navamsha?.houses ? <NorthIndianChart houses={k.navamsha.houses} /> : <p className="text-sm text-slate-500 py-8 text-center">Navamsha data not available.</p>}
          </TabsContent>
          <TabsContent value="planets"><PlanetaryPositions planets={k?.planets} ascendant={k?.ascendant} houses={k?.houses} /></TabsContent>
          <TabsContent value="dashas"><DashaPeriods dashas={k?.dashas} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default function SavedCharts({ onOpenSession }) {
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/kundli/saved`);
        if (res.data.success) setCharts(res.data.data);
      } catch (e) { toast.error("Failed to load saved sessions"); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/kundli/${id}`);
      setCharts((prev) => prev.filter((c) => c.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Session deleted");
    } catch (e) { toast.error("Failed to delete session"); }
  };

  const handleOpen = (chart) => { if (onOpenSession) onOpenSession(chart); };

  if (selected) return <ChartDetail selected={selected} onBack={() => setSelected(null)} onOpen={handleOpen} />;

  const counts = charts.reduce((acc, c) => ({ ...acc, [modeKey(c)]: (acc[modeKey(c)] || 0) + 1 }), {});
  const filterKeys = ["all", "personal", "compatibility", "lalkitab", ...(counts.tarot ? ["tarot"] : [])];
  const visible = filter === "all" ? charts : charts.filter((c) => modeKey(c) === filter);

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="saved-charts-page">
      <h1 className="font-cinzel text-2xl sm:text-3xl font-bold text-amber-400 tracking-wider mb-6">Saved Sessions</h1>
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-amber-400 animate-spin" /></div>
      ) : charts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Star className="h-12 w-12 text-slate-600" />
          <p className="text-slate-500">No saved sessions yet.</p>
          <p className="text-xs text-slate-600">Start a consultation and save your session to view it here.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 pb-2 mb-5" data-testid="session-filter-tabs">
            {filterKeys.map((key) => {
              const active = filter === key;
              const t = SESSION_TYPES[key];
              const count = key === "all" ? charts.length : counts[key] || 0;
              return (
                <button key={key} onClick={() => setFilter(key)} data-testid={`filter-tab-${key}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${active ? (t ? t.badge : "bg-amber-500/15 text-amber-300 border-amber-500/30") : "border-white/10 text-slate-400 hover:text-slate-200 hover:border-white/20"}`}>
                  {t && <t.Icon className="h-3.5 w-3.5" />}
                  {t ? t.short : "All"}
                  <span className="opacity-60" data-testid={`filter-count-${key}`}>{count}</span>
                </button>
              );
            })}
          </div>
          {visible.length === 0 ? (
            <p className="text-sm text-slate-500 py-16 text-center" data-testid="filter-empty-state">No {SESSION_TYPES[filter]?.label} sessions saved yet.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((chart) => (
                <SessionCard key={chart.id} chart={chart} onView={setSelected} onOpen={handleOpen} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
